<template>
    <v-bottom-sheet activator="#booking-dialog" width="600" persistent location="center"
        content-class="v-bottom-sheet rounded-t-xl">
        <template v-slot:default="{ isActive }">
            <v-stepper v-model="stepperSteps" flat mandatory>
                <template v-slot:default="{ prev, next }">
                    <v-card rounded="lg" elevation="2" class="booking-dialog">
                        <v-card-title class="d-flex justify-space-between align-center py-4 px-4">
                            <span class="text-h5 font-weight-bold">Book Now</span>
                            <v-btn icon="mdi-close" variant="text" density="comfortable"
                                @click="resetAndClose(isActive)"></v-btn>
                        </v-card-title>
                        <v-card-subtitle class="px-4 pb-4">
                            <span class="text-subtitle-1">{{ listing?.title }}</span>
                        </v-card-subtitle>

                        <!-- Improved stepper header with better mobile responsiveness -->
                        <v-stepper-header class="rounded-lg mx-2 mb-4 stepper-header">
                            <v-stepper-item color="primary" title="Contact" value="1" :complete="contactComplete"
                                :error="stepperSteps === '1' && showErrors && !isContactClean.value"></v-stepper-item>
                            <v-divider></v-divider>
                            <v-stepper-item color="primary" title="Date" value="2" :complete="dateComplete"
                                :error="stepperSteps === '2' && showErrors && !isDateClean.value"></v-stepper-item>
                            <v-divider></v-divider>
                            <v-stepper-item color="primary" title="Location" value="3" :complete="locationComplete"
                                :error="stepperSteps === '3' && showErrors && !isLocationClean.value"></v-stepper-item>
                            <v-divider></v-divider>
                            <v-stepper-item color="primary" title="Review" value="4" :complete="addedToCart"
                                :error="stepperSteps === '4' && formErrors.length > 0"></v-stepper-item>
                        </v-stepper-header>

                        <v-divider class="mb-4"></v-divider>

                        <!-- Improved stepper window with better spacing and visual hierarchy -->
                        <v-stepper-window>
                            <v-card-text class="px-0 py-0">
                                <!-- Step 1: Contact Information -->
                                <v-stepper-window-item value="1">
                                    <v-card prepend-icon="mdi-email" title="Contact Information" flat
                                        class="pa-3 rounded-lg bg-grey-lighten-5"
                                        subtitle="We'll send your confirmation voucher to these details">

                                        <v-form class="mt-2">
                                            <v-text-field density="comfortable" persistent-placeholder
                                                variant="outlined" required placeholder="Full Name"
                                                v-model="contactInfo.name" label="Name" prepend-inner-icon="mdi-account"
                                                class="mb-4" :rules="[(v) => !!v || 'Name is required']"></v-text-field>

                                            <v-text-field density="comfortable" v-model="contactInfo.mobile" required
                                                label="Mobile" id="bookingInfo-mobile" prepend-inner-icon="mdi-phone"
                                                class="mb-4" :rules="[(v) => !!v || 'Mobile Required']"
                                                persistent-placeholder variant="outlined"
                                                placeholder="Mobile Number"></v-text-field>

                                            <v-text-field persistent-placeholder variant="outlined"
                                                :rules="[(v) => !!v || 'Email is required']" dialog required
                                                id="bookingInfo-email" label="Email" v-model="contactInfo.email"
                                                class="mb-2" prepend-inner-icon="mdi-email-outline"
                                                placeholder="Email Address" type="email"></v-text-field>
                                        </v-form>
                                    </v-card>
                                </v-stepper-window-item>

                                <!-- Step 2: Date and Time -->
                                <v-stepper-window-item value="2">
                                    <v-card prepend-icon="mdi-calendar-clock-outline" title="Travel Date and Time" flat
                                        class="pa-3 rounded-lg bg-grey-lighten-5"
                                        subtitle="We'll send a calendar invite for your booking">
                                        <v-card-text class="px-0 py-1">
                                            <v-row class="mt-0">
                                                <v-col id="bookingInfo-details-traveldate" cols="12" md="6">
                                                    <v-text-field clearable label="Travel Date"
                                                        v-model="bookingInfo.details.traveldate" type="date"
                                                        :min="minDate" persistent-hint prepend-inner-icon="mdi-calendar"
                                                        persistent-placeholder
                                                        :rules="[(v) => !!v || 'Travel date is required']"
                                                        density="comfortable" variant="outlined"></v-text-field>
                                                </v-col>

                                                <v-col id="bookingInfo-details-time" cols="12" md="6">
                                                    <v-select v-if="listing.content.sched.length > 0"
                                                        :items="listing.content.sched" label="Departure Time"
                                                        :model-value="listing.content.sched[0]"
                                                        :rules="[(v) => !!v || 'Departure time is required']"
                                                        placeholder="Select Time" persistent-hint
                                                        prepend-inner-icon="mdi-clock-outline" persistent-placeholder
                                                        density="comfortable" v-model="bookingInfo.details.time"
                                                        variant="outlined"></v-select>

                                                    <v-text-field v-if="listing.content.sched.length == 0"
                                                        label="Departure Time" :model-value="bookingInfo.details.time"
                                                        v-model="bookingInfo.details.time" type="time"
                                                        :rules="[(v) => !!v || 'Departure time is required']"
                                                        id="bookingInfo-details-time" persistent-hint
                                                        prepend-inner-icon="mdi-clock-outline" persistent-placeholder
                                                        density="comfortable" variant="outlined"></v-text-field>
                                                </v-col>

                                                <v-col id="bookingInfo-details-pax" cols="12">
                                                    <v-text-field label="Number of People"
                                                        v-model="bookingInfo.details.pax"
                                                        :prefix="bookingInfo.details.pax > 1 ? ' persons' : ' person'"
                                                        type="number" persistent-hint max="10"
                                                        prepend-inner-icon="mdi-account-group" persistent-placeholder
                                                        density="comfortable" min="1" inputmode="numeric"
                                                        pattern="[0-9]*" variant="outlined"
                                                        @input="computeRate"></v-text-field>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-stepper-window-item>

                                <!-- Step 3: Location -->
                                <v-stepper-window-item value="3">
                                    <v-card prepend-icon="mdi-airplane-marker" title="Pick up & Drop Off" flat
                                        class="pa-3 rounded-lg bg-grey-lighten-5"
                                        subtitle="Specify your pick-up and drop-off locations">
                                        <v-row class="mt-0">
                                            <v-col cols="12">
                                                <div class="text-subtitle-2 font-weight-medium mb-2">Pickup Location
                                                </div>
                                                <google-places-auto-complete v-model="locationDetails.origin"
                                                    v-if="!defaultLocation.pickupAlert" label="Pickup Location"
                                                    placeholder="Search hotel or airport"
                                                    :bias="listing.origin"></google-places-auto-complete>
                                            </v-col>

                                            <v-col cols="12"
                                                v-if="locationDetails.origin?.place?.toLowerCase().includes('airport')">
                                                <v-text-field label="Flight Number"
                                                    placeholder="eg. 5J 226, Z2 424, PR 2781"
                                                    v-model="bookingInfo.details.pickup.flight"
                                                    prepend-inner-icon="mdi-airplane" persistent-hint
                                                    persistent-placeholder :rules="[(v) => !!v || 'Flight No.']"
                                                    density="comfortable" variant="outlined"></v-text-field>
                                            </v-col>

                                            <v-alert variant="tonal" type="info" density="comfortable"
                                                v-if="defaultLocation.pickupAlert" icon="mdi-map-marker"
                                                class="mt-2 mb-2" border="start" color="primary">
                                                Pickup location is set to <strong>{{
                                                    defaultLocation.shared[listing.origin] }}</strong>
                                            </v-alert>
                                        </v-row>

                                        <v-row class="mt-4">
                                            <v-col cols="12">
                                                <div class="text-subtitle-2 font-weight-medium mb-2">Drop-off Location
                                                </div>
                                                <google-places-auto-complete v-model="locationDetails.destination"
                                                    v-if="!defaultLocation.dropOffAlert && !defaultLocation.tourDropOffAlert"
                                                    label="Drop Off Location" placeholder="Search hotel or airport"
                                                    :bias="listing.destination"></google-places-auto-complete>
                                            </v-col>

                                            <v-col cols="12" v-if="defaultLocation.dropOffAlert">
                                                <v-alert variant="tonal" type="info" density="comfortable"
                                                    icon="mdi-map-marker" class="mt-2" color="primary" border="start">
                                                    Drop off location is set to <strong>{{
                                                        defaultLocation.shared[listing.destination] }}</strong>
                                                </v-alert>
                                            </v-col>
                                        </v-row>

                                        <v-row class="mt-2" v-if="defaultLocation.tourDropOffAlert">
                                            <v-col cols="12">
                                                <v-alert variant="tonal" type="info" density="comfortable"
                                                    icon="mdi-map-marker" class="mt-2" color="primary" border="start">
                                                    Drop off location is set to <strong>El Nido Boat Dock or El Nido
                                                        Beach</strong>
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </v-stepper-window-item>

                                <!-- Step 4: Review -->
                                <v-stepper-window-item value="4">
                                    <v-card prepend-icon="mdi-cash-multiple" title="Booking Summary" flat
                                        v-if="!addedToCart" class="pa-3 rounded-lg bg-grey-lighten-5"
                                        subtitle="Review your booking details before proceeding">
                                        <v-row class="mt-0">
                                            <v-list class="bg-transparent pa-0">
                                                <v-list-subheader class="text-primary font-weight-bold">SERVICE
                                                    DETAILS</v-list-subheader>
                                                <v-list-item class="rounded-lg mb-2">
                                                    <v-list-item-title class="font-weight-medium">{{ listing.title
                                                    }}</v-list-item-title>
                                                    <v-list-item-subtitle>Service Type</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>
                                                <v-list-subheader class="text-primary font-weight-bold">TRAVEL
                                                    INFORMATION</v-list-subheader>

                                                <v-list-item prepend-icon="mdi-map-marker" class="rounded-lg">
                                                    <v-list-item-title class="font-weight-medium">
                                                        {{ timeline.origin?.place?.toLowerCase().includes('airport') ?
                                                            `${timeline.origin.place} Flight:
                                                        (${bookingInfo.details.pickup.flight})` :
                                                            timeline.origin.place }}
                                                    </v-list-item-title>
                                                    <v-list-item-subtitle>Pickup in {{ timeline.origin.area
                                                    }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-list-item prepend-icon="mdi-map-marker-outline" class="rounded-lg">
                                                    <v-list-item-title class="font-weight-medium">{{
                                                        timeline.destination.place }}</v-list-item-title>
                                                    <v-list-item-subtitle>Drop Off in {{ timeline.destination.area
                                                    }}</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-list-item prepend-icon="mdi-calendar-clock" class="rounded-lg">
                                                    <v-list-item-title class="font-weight-medium">{{ travelDateDisplay
                                                    }}</v-list-item-title>
                                                    <v-list-item-subtitle>Travel Date & Time</v-list-item-subtitle>
                                                </v-list-item>

                                                <v-divider class="my-2"></v-divider>
                                                <v-list-subheader
                                                    class="text-primary font-weight-bold">PRICING</v-list-subheader>

                                                <v-list-item prepend-icon="mdi-cash-multiple"
                                                    class="rounded-lg bg-grey-lighten-4">
                                                    <v-list-item-title class="text-h6 font-weight-bold text-primary">
                                                        {{ formatCurrency(grandTotal, currency, locale) }}
                                                    </v-list-item-title>
                                                    <v-list-item-subtitle>
                                                        Total for {{ bookingInfo.details.pax }} {{
                                                            bookingInfo.details.pax > 1 ? 'persons' : 'person' }}
                                                    </v-list-item-subtitle>
                                                </v-list-item>
                                            </v-list>
                                        </v-row>

                                        <!-- Display form errors if any -->
                                        <v-expand-transition>
                                            <div v-if="formErrors.length > 0" class="mt-4">
                                                <v-alert type="error" variant="tonal"
                                                    title="Please correct the following issues:" density="comfortable">
                                                    <ul class="ps-4 mb-0">
                                                        <li v-for="(error, index) in formErrors" :key="index"
                                                            class="mb-1">
                                                            {{ error }}
                                                        </li>
                                                    </ul>
                                                </v-alert>
                                            </div>
                                        </v-expand-transition>
                                    </v-card>

                                    <!-- Success Card -->
                                    <v-card class="text-center pa-3 success-card rounded-lg bg-grey-lighten-5"
                                        v-if="addedToCart" flat>
                                        <v-icon size="120"
                                            class="text-success mb-4 mt-6">mdi-check-circle-outline</v-icon>
                                        <v-card-title class="d-flex justify-center">
                                            <h2 class="text-h5 font-weight-bold">Booking Added to Cart</h2>
                                        </v-card-title>
                                        <v-card-text>
                                            <p class="mb-6 text-subtitle-1">Your booking has been successfully added to
                                                the cart.</p>
                                            <div class="d-flex flex-wrap justify-center gap-2 mb-4">
                                                <v-btn color="primary" to="/cart" variant="elevated" size="large"
                                                    prepend-icon="mdi-cart">
                                                    View Cart
                                                </v-btn>
                                                <v-btn color="primary" @click="resetBookingDialog" variant="outlined"
                                                    size="large">
                                                    Book Another
                                                </v-btn>
                                                <v-btn color="secondary" @click="resetAndClose(isActive)" variant="text"
                                                    size="large">
                                                    Close
                                                </v-btn>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-stepper-window-item>
                            </v-card-text>
                        </v-stepper-window>

                        <!-- Navigation buttons -->
                        <v-card-actions class="pa-3" v-if="!addedToCart">
                            <v-btn @click="prev()" :disabled="stepperSteps === '1'" size="large" variant="outlined"
                                class="px-6" prepend-icon="mdi-arrow-left">Previous</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn :disabled="isNextDisabled" :loading="isProcessingBooking"
                                @click="stepperSteps === '4' ? finalizeBooking(isActive) : next()" size="large"
                                color="primary" variant="elevated" class="px-6" append-icon="mdi-arrow-right"
                                elevation="2">
                                {{ stepperSteps === "4" ? "Add to Cart" : "Next" }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </template>
            </v-stepper>
        </template>
    </v-bottom-sheet>
</template>

<script setup>
import { Destinations } from '~/data/Records';
import { ref, computed, watch, toRef, onMounted, nextTick } from 'vue';
import { useBookingStore } from '~/stores/bookingStore';
import { useCurrencyStore } from '~/stores/currencyStore';
import { storeToRefs } from 'pinia';
import { formatDate, getCurrentTime24Hour, formatCurrency, removeEmptyObjects, findKeysWithNullOrBlankValues, shaker } from '~/utils';


// Store and global state
const store = useBookingStore();
const dataStore = useCurrencyStore();
const { currency, locale, rates } = storeToRefs(dataStore);
const { gtag } = useGtag();

// Component state
const grandTotal = ref(0);
const searchData = toRef(() => store.getSearchData);
const contactInfo = ref(store.getBookerInfo);
const addedToCart = ref(false);
const stepperSteps = ref("1");
const geoLocationInfo = ref(null);
const doneCompute = ref(false);
const isProcessingBooking = ref(false);
const formErrors = ref([]);
const showConfirmation = ref(false);
const showErrors = ref(false);

// Add computed properties for step completion
const contactComplete = computed(() => parseInt(stepperSteps.value) > 1 || isContactClean.value);
const dateComplete = computed(() => parseInt(stepperSteps.value) > 2 || isDateClean.value);
const locationComplete = computed(() => parseInt(stepperSteps.value) > 3 || isLocationClean.value);

// Props
const props = defineProps({
    listingData: {
        type: Object,
        required: true
    }
});

const listing = toRef(() => props.listingData);

// Constants
const tourTypes = ['PRIVATE_TRANSFER_WITH_TOUR', 'JOINING_TOUR', 'PRIVATE_BOAT_TOUR', 'AIRPORT_TRANSFER', 'TOUR_PACKAGE_LOCAL', 'TOUR_PACKAGE_INTL'];

const minDate = new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0];
// Form data
const bookingInfo = ref({
    details: {
        service: '',
        code: '',
        pax: parseInt(searchData?.value?.pax) || 1,
        traveldate: minDate,
        time: '',
        pickup: {
            place: searchData?.value?.origin?.place || '',
            flight: '',
        },
        dropoff: {
            place: searchData?.value?.destination?.place || '',
        },
        leadGuest: contactInfo.value.name || '',
    },
});

// Location data
const locationDetails = ref({
    origin: searchData?.value?.origin || null,
    destination: searchData?.value?.destination || null,
});

// Timeline display data
const timeline = ref({
    origin: {
        place: '',
        area: ''
    },
    destination: {
        place: '',
        area: ''
    },
});

// Initialize booking info with listing data
const initializeBookingInfo = () => {
    try {
        if (listing.value) {
            bookingInfo.value.details.service = listing.value.title;
            bookingInfo.value.details.code = listing.value.code;

            // Initialize time based on available schedules
            if (listing.value.content?.sched) {
                bookingInfo.value.details.time = listing.value.content.sched.length === 0
                    ? getCurrentTime24Hour()
                    : listing.value.content.sched[0];
            }
        }
    } catch (error) {
        console.error('Error initializing booking info:', error);
    }
};

// Default location settings
const defaultLocation = computed(() => ({
    pickupAlert: listing.value?.listing_type === 'SHARED_TRANSFER' && ['ELN', 'PBT'].includes(listing.value?.origin),
    dropOffAlert: listing.value?.listing_type === 'SHARED_TRANSFER' && ['ELN', 'PBT'].includes(listing.value?.destination),
    shared: {
        ELN: 'El Nido Bus Terminal',
        PBT: 'Port Barton Terminal',
        PPS: 'Puerto Princesa - To be provided',
    },
    geoData: {
        ELN: {
            place_id: 'ChIJt-XuybJVtjMRCEsk4rdkzDQ',
            name: 'El Nido Bus Terminal',
            place: 'El Nido Bus Terminal',
            location: { lat: 11.1717192, lng: 119.3951534 },
        },
        PBT: {
            place_id: 'ChIJyRQU91_btTMRemWxdC8JD5Q',
            name: 'Port Barton Terminal',
            place: 'Port Barton Terminal',
            location: { lat: 10.442174, lng: 119.1158017 },
        },
    },
    tourDropOffAlert: listing.value?.listing_type === 'JOINING_TOUR' && listing.value?.destination === 'ELN',
    elNidoTourMeetup: {
        place_id: 'ChIJEVE9jRBVtjMRKWJhAKZYkF4',
        name: 'El Nido Beach',
        place: 'El Nido Beach',
        location: { lat: 11.1808983, lng: 119.3903687 },
    },
}));

// Improved contact validation
const isContactClean = computed(() => {
    if (!contactInfo.value) return false;
    const { name, mobile, email } = contactInfo.value;
    return Boolean(name && name.trim()) &&
        Boolean(mobile && mobile.trim()) &&
        Boolean(email && email.trim() && email.includes('@'));
});

// Improved location validation
const isLocationClean = computed(() => {
    // When pickup has default location
    if (defaultLocation.value.pickupAlert) {
        // Only need to check destination if drop-off is not using default location
        if (defaultLocation.value.dropOffAlert || defaultLocation.value.tourDropOffAlert) {
            return true; // Both locations are using defaults, so they're valid
        } else {
            return Boolean(locationDetails.value.destination?.place || locationDetails.value.destination?.name);
        }
    }

    // When drop-off has default location
    if (defaultLocation.value.dropOffAlert || defaultLocation.value.tourDropOffAlert) {
        return Boolean(locationDetails.value.origin?.place || locationDetails.value.origin?.name);
    }

    // No default locations, check both origin and destination
    return Boolean(locationDetails.value.origin?.place || locationDetails.value.origin?.name) &&
        Boolean(locationDetails.value.destination?.place || locationDetails.value.destination?.name);
});

const isDateClean = computed(() => {
    const { traveldate, time, pax } = bookingInfo.value.details;
    return traveldate && time && pax > 0;
});

const travelDateDisplay = computed(() => {
    const date = new Date(bookingInfo.value.details.traveldate + ' ' + bookingInfo.value.details.time);
    function formatDate(date) {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        const day = date.getDate();
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';

        hours = hours % 12;
        hours = hours ? hours : 12;

        const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

        const formattedDate = monthNames[monthIndex] + ' ' + day + ', ' + year + ' ' + hours + ':' + formattedMinutes + ampm;
        return formattedDate;
    }

    return formatDate(date);

});

const isNextDisabled = computed(() => {
    switch (stepperSteps.value) {
        case '1':
            return !isContactClean.value;
        case '2':
            return !isDateClean.value;
        case '3':
            return !isLocationClean.value;
        default:
            return false;
    }
});

// Rate calculation
const computeRate = () => {
    try {
        doneCompute.value = false;
        grandTotal.value = 0;

        if (!listing.value?.ratePlans || !currency.value || !listing.value?.ratePlans[currency.value]) {
            console.error('Missing rate plans data for calculation');
            return 0;
        }

        const { ratePlans } = listing.value;
        const { base, inc, extra } = ratePlans[currency.value];
        const pax = parseInt(bookingInfo.value.details.pax) || 1;
        let rate = pax <= inc ? base : base + (pax - inc) * extra;

        if (!geoLocationInfo.value || !locationDetails.value.origin || !locationDetails.value.destination) {
            grandTotal.value = rate;
            return rate;
        }

        // Calculate extra charges
        let pickupCharge = geoLocationInfo.value?.origin?.location?.sublocationInfo?.charge || 0;
        let dropoffCharge = geoLocationInfo.value?.destination?.location?.sublocationInfo?.charge || 0;
        let extraCharges = 0;

        // Apply special cases for charges
        if (listing.value.listingType === 'PRIVATE_TRANSFER' || listing.value.listingType === 'SHARED_TRANSFER') {
            if (geoLocationInfo.value?.destination?.location?.code === 'AST') {
                dropoffCharge = 0;
            }
            if (geoLocationInfo.value?.origin?.location?.code === 'AST') {
                pickupCharge = 0;
            }
        }
        if (listing.value.code?.includes('UR')) {
            if (geoLocationInfo.value?.destination?.location?.code === 'SAB') {
                dropoffCharge = 0;
            }
            if (geoLocationInfo.value?.origin?.location?.code === 'SAB') {
                pickupCharge = 0;
            }
        }

        extraCharges = pickupCharge + dropoffCharge;
        rate = parseFloat(rate) + parseFloat(currencyConverter(rates.value, extraCharges, 'USD', currency.value));


        grandTotal.value = rate;
        doneCompute.value = true;

        // Update timeline display
        updateTimeline();

        return rate;
    } catch (error) {
        console.error('Error computing rate:', error);
        return 0;
    }
};

// Update timeline display data
const updateTimeline = () => {
    try {
        const originArea = Destinations.find((i) => i.code === listing.value?.origin)?.title || '';
        const destinationArea = Destinations.find((i) => i.code === listing.value?.destination)?.title || '';

        timeline.value = {
            origin: {
                place: locationDetails?.value?.origin?.place || locationDetails?.value?.origin?.name || '',
                area: originArea,
            },
            destination: {
                place: locationDetails?.value?.destination?.place || locationDetails?.value?.destination?.name || '',
                area: destinationArea,
            },
        };
    } catch (error) {
        console.error('Error updating timeline:', error);
    }
};

// Update fetch location data function
const fetchLocationData = async () => {
    try {
        grandTotal.value = 0;

        // Apply default locations if needed
        if (defaultLocation.value.pickupAlert) {
            locationDetails.value.origin = defaultLocation.value.geoData[listing.value.origin];
        }
        if (defaultLocation.value.dropOffAlert) {
            locationDetails.value.destination = defaultLocation.value.geoData[listing.value.destination];
        }
        if (defaultLocation.value.tourDropOffAlert) {
            locationDetails.value.destination = defaultLocation.value.elNidoTourMeetup;
        }

        // Clear location-related errors
        formErrors.value = formErrors.value.filter(err => !err.includes('location'));

        // Special validation for airports requiring flight numbers
        if (locationDetails.value.origin?.place?.toLowerCase().includes('airport') &&
            !bookingInfo.value.details.pickup.flight) {
            formErrors.value.push('Flight number required for airport pickup');
        }

        // Validate location data before API call - checking for actual place names
        if (!isLocationClean.value) {
            formErrors.value.push('Missing or incomplete pickup or drop-off locations');
            return;
        }

        // Prepare API request data - ensure we send valid data
        const requestData = {
            origin: locationDetails.value.origin,
            destination: locationDetails.value.destination
        };

        // Only call API if we have valid place data in both origin and destination
        const response = await fetch('/api/places/get-location', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        geoLocationInfo.value = await response.json();
        computeRate();

        // Mark locations as clean after successful API call
        showErrors.value = false;
    } catch (error) {
        console.error('Error fetching location data:', error);
        formErrors.value.push('Failed to fetch location data. Please try again later.');
    }
};

// Process booking with validation error feedback
const processBooking = async () => {
    try {
        // Show validation errors if any exist
        showErrors.value = true;

        // Proceed only if all data is valid
        if (!isContactClean.value) {
            stepperSteps.value = "1";
            formErrors.value = ['Please complete contact information'];
            return;
        }

        if (!isDateClean.value) {
            stepperSteps.value = "2";
            formErrors.value = ['Please complete travel date and time'];
            return;
        }

        if (!isLocationClean.value) {
            stepperSteps.value = "3";
            formErrors.value = ['Please complete pickup and drop-off locations'];
            return;
        }

        formErrors.value = [];

        // Deep copy booking info to prevent mutation
        const payload = JSON.parse(JSON.stringify(bookingInfo.value));

        // Validate listing data
        if (!listing.value || !listing.value.code) {
            formErrors.value.push('Missing listing information');
            return;
        }

        // Add details to payload
        payload.details.code = listing.value.code;
        payload.details.service = listing.value.title;
        payload.details.operator = listing.value.operator;
        payload.details.rate = grandTotal.value;
        payload.details.ratePlan = listing.value.ratePlans[currency.value];
        payload.details.total = grandTotal.value;

        // Add service provider details
        if (listing.value.serviceProvider) {
            const { id, name } = listing.value.serviceProvider;
            payload.details.serviceProvider = { id, name };
        } else {
            formErrors.value.push('Missing service provider information');
            return;
        }

        // Add customer details
        payload.details.leadGuest = contactInfo.value.name || '';

        // Add participants array for tour types
        if (tourTypes.includes(listing.value.listing_type)) {
            payload.details.participants = [];
        }

        // Format pickup/dropoff location details
        if (locationDetails.value.origin && locationDetails.value.destination) {
            const originPlace = (locationDetails.value.origin.place || locationDetails.value.origin.name || '').toLowerCase();
            payload.details.pickup = originPlace.includes('airport')
                ? `${locationDetails.value.origin.place} (${bookingInfo.value.details.pickup.flight})`
                : `${locationDetails.value.origin.place}`;
            payload.details.dropoff = locationDetails.value.destination.place || locationDetails.value.destination.name || '';
        } else {
            formErrors.value.push('Missing location information');
            return;
        }

        // Validate all required fields
        const errors = findKeysWithNullOrBlankValues(payload);

        if (errors.length > 0) {
            formErrors.value = errors.map(error => `Missing required field: ${error}`);
            nextTick(() => {
                const errorId = `bookingInfo-${errors[0].replace('.', '-')}`;
                const element = document.getElementById(errorId);
                if (element) {
                    element.scrollIntoView();
                    setTimeout(() => {
                        shaker(errorId);
                    }, 500);
                }
            });
        } else {
            // Instead of immediately adding to cart, show confirmation dialog
            showConfirmation.value = true;
        }
    } catch (error) {
        console.error('Error processing booking:', error);
        formErrors.value.push('Failed to process your booking. Please try again.');
    }
};

// Update the finalizeBooking function to use gtag directly
const finalizeBooking = async (isActive) => {
    try {
        isProcessingBooking.value = true;

        // Deep copy booking info
        const payload = JSON.parse(JSON.stringify(bookingInfo.value));

        // Add necessary details
        payload.details.code = listing.value.code;
        payload.details.service = listing.value.title;
        payload.details.operator = listing.value.operator;
        payload.details.rate = grandTotal.value;
        payload.details.ratePlan = listing.value.ratePlans[currency.value];
        payload.details.total = grandTotal.value;

        // Add service provider
        const { id, name } = listing.value.serviceProvider;
        payload.details.serviceProvider = { id, name };
        payload.details.leadGuest = contactInfo.value.name || '';

        // Add participants if needed
        if (tourTypes.includes(listing.value.listing_type)) {
            payload.details.participants = [];
        }

        // Format pickup/dropoff
        const originPlace = (locationDetails.value.origin.place || locationDetails.value.origin.name || '').toLowerCase();
        payload.details.pickup = originPlace.includes('airport')
            ? `${locationDetails.value.origin.place} (${bookingInfo.value.details.pickup.flight})`
            : `${locationDetails.value.origin.place}`;
        payload.details.dropoff = locationDetails.value.destination.place || locationDetails.value.destination.name || '';

        // Track event using gtag directly
        try {
            gtag('event', 'AddToCart', {
                listing_id: listing.value.code,
                listing_name: listing.value.title,
                currency: currency.value,
                value: grandTotal.value
            });
        } catch (err) {
            console.error('Error tracking event:', err);
        }

        // Add to cart
        addedToCart.value = true;
        showConfirmation.value = false;
        store.addCartItem(removeEmptyObjects(payload.details));

    } catch (error) {
        console.error('Error finalizing booking:', error);
        formErrors.value.push('Failed to add your booking to cart. Please try again.');
    } finally {
        isProcessingBooking.value = false;
    }
};

// Add a function to reset the booking dialog
const resetBookingDialog = () => {
    stepperSteps.value = "1";
    addedToCart.value = false;
    showConfirmation.value = false;
    formErrors.value = [];
    showErrors.value = false;

    // Reset contact info
    contactInfo.value = { ...store.getBookerInfo };

    // Reset booking info
    bookingInfo.value = {
        details: {
            service: '',
            code: '',
            pax: parseInt(searchData?.value?.pax) || 1,
            traveldate: minDate,
            time: '',
            pickup: {
                place: searchData?.value?.origin?.place || '',
                flight: '',
            },
            dropoff: {
                place: searchData?.value?.destination?.place || '',
            },
            leadGuest: contactInfo.value.name || '',
        },
    };

    // Reset location data
    locationDetails.value = {
        origin: searchData?.value?.origin || null,
        destination: searchData?.value?.destination || null,
    };

    // Reinitialize with fresh data
    initializeBookingInfo();
    initializeLocationData();
};

// Add a function to initialize location data
const initializeLocationData = () => {
    // Set default locations if needed
    if (defaultLocation.value.pickupAlert) {
        locationDetails.value.origin = { ...defaultLocation.value.geoData[listing.value.origin] };
    }

    if (defaultLocation.value.dropOffAlert) {
        locationDetails.value.destination = { ...defaultLocation.value.geoData[listing.value.destination] };
    }

    if (defaultLocation.value.tourDropOffAlert) {
        locationDetails.value.destination = { ...defaultLocation.value.elNidoTourMeetup };
    }

    // Update geoLocationInfo with default locations
    if (defaultLocation.value.pickupAlert || defaultLocation.value.dropOffAlert || defaultLocation.value.tourDropOffAlert) {
        geoLocationInfo.value = {
            ...(geoLocationInfo.value || {}),
            origin: defaultLocation.value.pickupAlert ? defaultLocation.value.geoData[listing.value.origin] : locationDetails.value.origin,
            destination: defaultLocation.value.dropOffAlert ? defaultLocation.value.geoData[listing.value.destination] :
                (defaultLocation.value.tourDropOffAlert ? defaultLocation.value.elNidoTourMeetup : locationDetails.value.destination)
        };

        // If both locations are set, calculate rate
        if (locationDetails.value.origin && locationDetails.value.destination) {
            computeRate();
        }
    }
};

// Lifecycle hooks
onMounted(() => {
    initializeBookingInfo();
    initializeLocationData();
});

// Update the locationDetails watcher to better handle validation
watch(
    locationDetails,
    (value) => {
        if (value.origin && value.destination) {
            bookingInfo.value.details.pickup = { ...bookingInfo.value.details.pickup };

            // Add place information if available
            if (value.origin.place || value.origin.name) {
                bookingInfo.value.details.pickup.place = value.origin.place || value.origin.name;
            }

            // Set dropoff details
            bookingInfo.value.details.dropoff = {
                place: value.destination.place || value.destination.name || ''
            };

            // Only fetch location data if we have valid place data
            if ((value.origin.place || value.origin.name) &&
                (value.destination.place || value.destination.name)) {
                formErrors.value = formErrors.value.filter(err => !err.includes('location'));
                fetchLocationData();
            }
        }
    },
    { deep: true }
);

// Watchers
watch(
    bookingInfo,
    () => {
        computeRate();
    },
    { deep: true }
);

watch(currency, () => {
    computeRate();
});

// Update the validation when moving between steps
watch(stepperSteps, (newStep) => {
    if (newStep === "1" && !isContactClean.value) {
        showErrors.value = true;
    } else if (newStep === "2" && !isDateClean.value) {
        showErrors.value = true;
    } else if (newStep === "3" && !isLocationClean.value) {
        showErrors.value = true;
    }
});

// Update store with contactInfo changes
watch(contactInfo, (newValue) => {
    if (newValue) {
        store.setBookerInfo(newValue);
    }
}, { deep: true });

// Add function to reset and close the bottom sheet
const resetAndClose = (isActive) => {
    resetBookingDialog();
    if (isActive && typeof isActive.value !== 'undefined') {
        isActive.value = false;
    }
};
</script>

<style scoped>
.booking-dialog {
    border-radius: 12px;
    overflow: hidden;
}

.v-stepper-header {
    border: 1px solid rgba(0, 0, 0, 0.12);
}

.v-btn {
    letter-spacing: 0.5px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.v-card.success-card .v-icon {
    animation: success-bounce 1s ease;
}

@keyframes success-bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-20px);
    }

    60% {
        transform: translateY(-10px);
    }
}

.v-list-item {
    transition: background-color 0.2s ease;
}

.v-list-item:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

/* Improve stepper indicator visibility */
.v-stepper-item--active {
    font-weight: bold;
}

/* Add transitions between steps */
.v-stepper-window-item {
    transition: all 0.3s ease-in-out;
}

/* Mobile optimizations */
@media (max-width: 600px) {
    .v-bottom-sheet {
        max-height: 100% !important;
    }

    .v-list-item {
        padding: 8px 12px;
    }

    .v-card-title {
        font-size: 1.25rem !important;
    }

    .v-card-subtitle {
        font-size: 0.875rem !important;
    }

    .v-list-subheader {
        font-size: 0.75rem !important;
        padding: 6px 12px;
    }
}

/* Responsive stepper header */
.stepper-header {
    width: 100%;
}

/* Mobile optimizations */
@media (max-width: 600px) {
    .v-bottom-sheet {
        max-height: 100% !important;
    }

    .v-stepper-header {
        margin: 0 0 16px 0 !important;
        padding: 0 !important;
    }

    /* Make stepper items smaller on mobile */
    :deep(.v-stepper-item) {
        flex: 1;
        min-width: auto !important;
        padding: 0 4px !important;
    }

    /* Adjust spacing for stepper indicators on mobile */
    :deep(.v-stepper-item__avatar) {
        margin: 0 auto !important;
    }

    /* Make title text smaller on mobile */
    :deep(.v-stepper-item__title) {
        font-size: 0.75rem !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        margin: 0 auto !important;
        text-align: center;
    }

    /* Remove unnecessary duplicate mobile styles */
    .step-title {
        /* This class is no longer needed */
        display: inline;
        /* Ensure it's visible */
    }

    /* Rest of mobile optimizations remain */
    .v-list-item {
        padding: 8px 12px;
    }

    .v-card-title {
        font-size: 1.25rem !important;
    }

    .v-card-subtitle {
        font-size: 0.875rem !important;
    }

    .v-list-subheader {
        font-size: 0.75rem !important;
        padding: 6px 12px;
    }

    /* Reduce spacing in heading area */
    .v-card-title.py-4 {
        padding-top: 12px !important;
        padding-bottom: 8px !important;
    }

    .v-card-subtitle.pb-4 {
        padding-bottom: 12px !important;
    }
}
</style>